import { FormGroup, FormLabel, Grid, Stack, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import * as assetApi from '../../api/app/assetApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { CheckBox } from '../../components/core/CheckBox/CheckBox';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { Asset, MainDashboardInputs } from '../../types/app/asset.types';
import { dictionary } from '../../utils/core/dictionary';
interface Props {
  onSubmit: SubmitHandler<MainDashboardInputs>;
  defaultValues?: MainDashboardInputs;
  isReadOnly: boolean;
  isUsedHydra: boolean;
}

export function MainDashboardForm({ onSubmit, defaultValues, isReadOnly, isUsedHydra }: Props) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<MainDashboardInputs>();

  const {
    data: assetList,
    isLoading,
    isError,
  } = useQuery<Asset[]>(['assets'], () => assetApi.getAssetList());

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, reset]);

  if (isError) return <ApiError />;
  if (isLoading || !assetList) return <Loader />;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Time aggregation"
              {...register('timeAggregation', { required: 'Time aggregation is required.' })}
              error={Boolean(errors.timeAggregation)}
              helperText={errors.timeAggregation?.message}
              disabled={isReadOnly}
              autoFocus
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              InputProps={{ inputProps: { min: 0.0, max: 1.0, step: '0.01' } }}
              label="Qty level green"
              {...register('qtyLevelGreen')}
              error={Boolean(errors.qtyLevelGreen)}
              helperText={errors.qtyLevelGreen?.message}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              InputProps={{ inputProps: { min: 0.0, max: 1.0, step: '0.01' } }}
              label="Qty level orange"
              {...register('qtyLevelOrange')}
              error={Boolean(errors.qtyLevelOrange)}
              helperText={errors.qtyLevelOrange?.message}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Takt time line Qty"
              {...register('taktTimeLineQty')}
              error={Boolean(errors.taktTimeLineQty)}
              helperText={errors.taktTimeLineQty?.message}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              label="Note"
              {...register('note')}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckBox
              label={'Show orange level line'}
              controllerProps={{
                name: 'showOrangeLevelLine',
                control: control,
              }}
              errors={errors}
              readOnly={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckBox
              label={'Show green level line'}
              controllerProps={{
                name: 'showGreenLevelLine',
                control: control,
              }}
              errors={errors}
              readOnly={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckBox
              label={'Show scraps'}
              controllerProps={{
                name: 'showScrap',
                control: control,
              }}
              errors={errors}
              readOnly={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckBox
              label={'Show actual cycle time'}
              controllerProps={{
                name: 'showActualCycleTime',
                control: control,
              }}
              errors={errors}
              readOnly={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckBox
              label={'Show state'}
              controllerProps={{
                name: 'showActualState',
                control: control,
              }}
              errors={errors}
              readOnly={isReadOnly}
            />
          </Grid>

          {isUsedHydra && (
            <>
              <Grid item xs={12} sx={{ border: '1px solid #858585', borderRadius: '5px' }}>
                <FormLabel component="legend">{dictionary.HYDRA_CHECKBOX_OPTIONS}</FormLabel>
                <FormGroup>
                  <CheckBox
                    label={dictionary.HYDRA_SHOW_MACHINE_NUMBER}
                    controllerProps={{
                      name: 'showHydraMachineNumber',
                      control: control,
                    }}
                    errors={errors}
                    readOnly={isReadOnly}
                  />
                  <CheckBox
                    label={dictionary.HYDRA_SHOW_ORDER_NUMBER}
                    controllerProps={{
                      name: 'showHydraOrderNumber',
                      control: control,
                    }}
                    errors={errors}
                    readOnly={isReadOnly}
                  />
                  <CheckBox
                    label={dictionary.HYDRA_SHOW_TOOL_NUMBER}
                    controllerProps={{
                      name: 'showHydraToolNumber',
                      control: control,
                    }}
                    errors={errors}
                    readOnly={isReadOnly}
                  />
                  <CheckBox
                    label={dictionary.HYDRA_SHOW_PART_NUMBER}
                    controllerProps={{
                      name: 'showHydraPartNumber',
                      control: control,
                    }}
                    errors={errors}
                    readOnly={isReadOnly}
                  />
                  <CheckBox
                    label={dictionary.HYDRA_SHOW_STATUS}
                    controllerProps={{
                      name: 'showHydraStatus',
                      control: control,
                    }}
                    errors={errors}
                    readOnly={isReadOnly}
                  />
                </FormGroup>
              </Grid>
            </>
          )}
          {!isReadOnly && (
            <Grid item>
              <Stack spacing={2} direction="row">
                <Button type="submit" variant="contained" color="success">
                  {t('general.action-buttons.save')}
                </Button>
              </Stack>
            </Grid>
          )}
        </Grid>
      </form>
    </>
  );
}
